import { action } from 'satcheljs';
import {
    ActiveRetentionPolicyRowType,
    AvailableRetentionPolicyRowType,
} from '../store/schema/RetentionPolicyRowType';

/**
 * Action dispatched when the Retention policies component is loaded
 */
export const onRetentionPoliciesFullInitialized = action('onRetentionPoliciesFullInitialized');

/**
 * Action on the click of save button
 */
export const saveRetentionPolicies = action('saveRetentionPolicies');

/**
 * Action on the click of discard button
 */
export const resetRetentionPoliciesEdit = action('resetRetentionPoliciesEdit');

/**
 * Action dispatched when active policies request is sent
 */
export const onActivePoliciesRequestSent = action('onActivePoliciesRequestSent');

/**
 * Action dispatched when active policies request is completed
 */
export const onActivePoliciesRequestComplete = action('onActivePoliciesRequestComplete');

/**
 * Action dispatched when active policies request throws error or we reach an invalid state
 */
export const onActivePoliciesRequestError = action('onActivePoliciesRequestError');

/**
 * Action dispatched when available policies request is sent
 */
export const onAvailablePoliciesRequestSent = action('onAvailablePoliciesRequestSent');

/**
 * Action dispatched when available policies request is completed
 */
export const onAvailablePoliciesRequestComplete = action('onAvailablePoliciesRequestComplete');

/**
 * Action dispatched when available policies request throws error or we reach an invalid state
 */
export const onAvailablePoliciesRequestError = action('onAvailablePoliciesRequestError');

/**
 * Action dispatched when active policies page is loaded
 */
export const activePoliciesPageLoaded = action('activePoliciesPageLoaded');

/**
 * Action dispatched when available policies page is loaded
 */
export const availablePoliciesPageLoaded = action('availablePoliciesPageLoaded');

/**
 * Action dispatched when request to add policies to active tags is complete
 */
export const onAddActivePolicyTagsRequestComplete = action('onAddActivePolicyTagsRequestComplete');

/**
 * Action dispatched when user clicks on 'Add new policy' button
 */
export const switchToAvailablePolicies = action('switchToAvailablePolicies');

/**
 * Action dispatched when available policies page is unmounted
 */
export const availablePoliciesPageUnmounted = action('availablePoliciesPageUnmounted');

/**
 * Action to set active retention policies list
 */
export const onActiveRetentionPoliciesListSucceeded = action(
    'onActiveRetentionPoliciesListSucceeded',
    (policies: { [key: string]: ActiveRetentionPolicyRowType }, policyRowIds: string[]) => ({
        policies,
        policyRowIds,
    })
);

/**
 * Action to set available retention policies list
 */
export const onAvailableRetentionPoliciesListSucceeded = action(
    'onAvailableRetentionPoliciesListSucceeded',
    (policies: { [key: string]: AvailableRetentionPolicyRowType }, policyRowIds: string[]) => ({
        policies,
        policyRowIds,
    })
);

/**
 * Action to check/uncheck policies from the available policies list
 */
export const enableDisableRetentionPolicy = action(
    'enableDisableRetentionPolicy',
    (retentionId: string, isChecked: boolean) => ({ retentionId, isChecked })
);

/**
 * Action listened by the orchestrator to remove policy from the active retention tags
 */
export const removeRetentionPolicy = action('removeRetentionPolicy', (retentionId: string) => ({
    retentionId,
}));

/**
 * Action to mutate the active policies list by removing the policy row
 */
export const deleteRetentionPolicyRow = action(
    'deleteRetentionPolicyRow',
    (retentionId: string, policyIndex: number) => ({ retentionId, policyIndex })
);

/**
 * Action to add policy to the active policies list
 * This is currently performed only when delete call on active policies fails and we want to add that back to the list
 */
export const addRetentionPolicyRow = action(
    'addRetentionPolicyRow',
    (policyRow: ActiveRetentionPolicyRowType, retentionId: string, policyIndex: number) => ({
        policyRow,
        retentionId,
        policyIndex,
    })
);

/**
 * Action to update the user configuration when a policy is removed
 */
export const updateUserConfigurationOnRemovePolicy = action(
    'updateUserConfigurationOnRemovePolicy',
    (retentionPolicyRows: ActiveRetentionPolicyRowType[]) => ({ retentionPolicyRows })
);

/**
 * Action to update the user configuration when a policy is added
 */
export const updateUserConfigurationOnAddPolicy = action(
    'updateUserConfigurationOnAddPolicy',
    (retentionPolicyRows: AvailableRetentionPolicyRowType[]) => ({ retentionPolicyRows })
);
